// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type TaskDefinition, type TaskKind, type TasksSortBy, useTasksQuery } from "@/api/hooks/use-tasks";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { PageHeader } from "@/components/layout/page-header";
import { CopyableKey } from "@/components/shared/copyable-key";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterPresets } from "@/components/shared/filter-presets";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { KeyChip } from "@/components/shared/key-chip";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TaskActionsMenu } from "@/features/tasks/task-actions-menu";
import { TaskIcon } from "@/features/tasks/task-icon";
import { TaskKindBadge } from "@/features/tasks/task-kind-badge";
import { ToggleTaskActiveDialog } from "@/features/tasks/toggle-task-active-dialog";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/tasks/index";

const FILTER_DEFAULTS = {
  search: "",
  onlyActive: true,
  kind: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "fullName",
  sortDesc: false,
};

export function TasksList() {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.tasksEdit);
  const [toggleTask, setToggleTask] = useState<TaskDefinition | null>(null);
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);
  const [kindFilter, setKindFilter] = useState(initial.kind);

  const sortBy = (sorting[0]?.id ?? "fullName") as TasksSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);
  const isFiltered = !!search || !onlyActive || !!kindFilter;

  const clearFilters = useCallback(() => {
    setSearch("");
    setOnlyActive(true);
    setKindFilter("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters, disabled: toggleTask !== null });

  // The single description of "what is filtered right now" — shared by the URL sync and the presets.
  const currentFilters = useMemo(
    () => ({
      search,
      onlyActive,
      kind: kindFilter,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortId: sorting[0]?.id ?? "fullName",
      sortDesc: sorting[0]?.desc ?? false,
    }),
    [search, onlyActive, kindFilter, pagination.pageIndex, pagination.pageSize, sorting],
  );

  const syncUrl = useCallback(() => {
    const encoded = encodeFilters(currentFilters, FILTER_DEFAULTS);
    navigate({ to: "/tasks", search: encoded ? { f: encoded } : {}, replace: true });
  }, [currentFilters, navigate]);

  const applyPreset = useCallback(function applyPreset(next: typeof FILTER_DEFAULTS) {
    setSearch(next.search);
    setOnlyActive(next.onlyActive);
    setKindFilter(next.kind);
    setSorting([{ id: next.sortId, desc: next.sortDesc }]);
    // A preset carries no page index, and page size stays the viewer's own preference.
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const { data, isLoading, refetch, isFetching } = useTasksQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyActive: onlyActive || undefined,
    kind: (kindFilter || undefined) as TaskKind | undefined,
  });

  const rows = data?.tasks ?? [];

  const columns = useMemo<DataTableColumnDef<TaskDefinition>[]>(
    () => [
      {
        accessorKey: "kind",
        header: t("tasks.list.columns.kind"),
        size: 96,
        enableHiding: false,
        meta: { columnLabel: t("tasks.list.columns.kind"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <TaskKindBadge kind={row.original.kind} />,
      },
      {
        accessorKey: "customTaskDefinitionId",
        header: t("tasks.list.columns.id"),
        size: 80,
        meta: { columnLabel: t("tasks.list.columns.id"), forceWidth: true, defaultHidden: true },
        cell: ({ row }) =>
          row.original.customTaskDefinitionId != null ? (
            <RowId id={row.original.customTaskDefinitionId} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "fullName",
        header: t("tasks.list.columns.name"),
        meta: { columnLabel: t("tasks.list.columns.name") },
        cell: ({ row, table }) => {
          // Surface the key as a sub-line when its dedicated column is hidden from the view.
          const keyColumnVisible = table.getColumn("key")?.getIsVisible() ?? false;
          return (
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded"
                style={{ backgroundColor: row.original.color ?? "#64748b" }}
              >
                <TaskIcon icon={row.original.icon} color="#ffffff" className="h-4 w-4" />
              </span>
              <div className="flex min-w-0 flex-col">
                <Link
                  to="/tasks/$subtype"
                  params={{ subtype: row.original.key }}
                  className="w-fit font-medium text-primary hover:underline"
                >
                  {row.original.fullName}
                </Link>
                {!keyColumnVisible && <CopyableKey value={row.original.key} />}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "key",
        header: t("tasks.list.columns.key"),
        meta: { columnLabel: t("tasks.list.columns.key"), defaultHidden: true },
        cell: ({ row }) => <KeyChip label={row.original.key} />,
      },
      {
        accessorKey: "shortName",
        header: t("tasks.list.columns.shortName"),
        meta: { columnLabel: t("tasks.list.columns.shortName") },
        cell: ({ row }) => row.original.shortName || <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: "acronym",
        header: t("tasks.list.columns.acronym"),
        size: 90,
        meta: { columnLabel: t("tasks.list.columns.acronym") },
        cell: ({ row }) =>
          row.original.acronym ? (
            <Badge variant="outline" className="font-mono font-normal">
              {row.original.acronym}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "algoFillAction",
        header: t("tasks.list.columns.algoFill"),
        meta: { columnLabel: t("tasks.list.columns.algoFill"), defaultHidden: true },
        cell: ({ row }) =>
          row.original.algoFillAction ? (
            <span className="font-mono text-xs">{row.original.algoFillAction}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "stores",
        header: t("tasks.list.columns.stores"),
        size: 110,
        enableSorting: false,
        meta: { columnLabel: t("tasks.list.columns.stores") },
        cell: ({ row }) =>
          row.original.settingsCount != null ? (
            <span className="font-mono text-xs tabular-nums">
              {row.original.activeSettingsCount}/{row.original.settingsCount}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "updatedAt",
        header: t("tasks.list.columns.updatedAt"),
        meta: { columnLabel: t("tasks.list.columns.updatedAt") },
        cell: ({ row }) => <DateValue value={row.original.updatedAt} />,
      },
      {
        id: "isActive",
        header: t("tasks.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("tasks.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      ...(canEdit
        ? [
            {
              // `actions` is the id DataTable looks for to host the column-visibility switcher,
              // so this sticky trailing column has to carry that exact id.
              id: "actions",
              size: 50,
              meta: { forceWidth: true, sticky: "right" },
              enableSorting: false,
              enableHiding: false,
              cell: ({ row }) =>
                row.original.kind === "custom" ? (
                  <TaskActionsMenu
                    isActive={row.original.isActive}
                    onToggleActive={() => setToggleTask(row.original)}
                  />
                ) : null,
            } satisfies DataTableColumnDef<TaskDefinition>,
          ]
        : []),
    ],
    [t, canEdit],
  );

  return (
    <div>
      <PageHeader title={t("tasks.title")} />
      <div className="p-6">
        <DataTable
          storageKey="tasks-list"
          columns={columns}
          data={rows}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={1000}
          onRefresh={refetch}
          isRefreshing={isFetching}
          searchPlaceholder={t("tasks.list.searchPlaceholder")}
          presets={
            <FilterPresets
              storageKey="tasks-list"
              defaults={FILTER_DEFAULTS}
              current={currentFilters}
              onApply={applyPreset}
            />
          }
          filters={
            <>
              <FilterSelect
                label={t("tasks.list.columns.kind")}
                value={kindFilter}
                onChange={(value) => {
                  setKindFilter(value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={[
                  { label: t("tasks.kind.custom"), value: "custom" },
                  { label: t("tasks.kind.builtIn"), value: "builtIn" },
                ]}
                searchPlaceholder={t("tasks.list.filter.searchKinds")}
                emptyText={t("tasks.list.filter.noKinds")}
              />
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
        />
      </div>

      <ToggleTaskActiveDialog
        task={
          toggleTask
            ? {
                subtype: toggleTask.key,
                name: toggleTask.fullName,
                isActive: toggleTask.isActive,
              }
            : null
        }
        onClose={() => setToggleTask(null)}
      />
    </div>
  );
}
